import { useQuery } from "@apollo/client";
import { ItemCategoryLevel } from "../../../inventory/type";
import { CategoryQueriesProps, CategoryQueriesResult } from "./types";
import { GetItemCategoriesForLookup } from "../../../inventory/graphql/item-category.graphql";
import {
  QueryPageableRequest,
  QueryPageableResponse,
} from "../../../../core/type";
import { ItemCategory, ItemCategoryFindRequest } from "../../../inventory/type";

export const useCategoryQueries = ({
  categoryId,
  subcategoryId,
}: CategoryQueriesProps): CategoryQueriesResult => {
  // Category queries
  const { data: categoryData, refetch: refetchCategory } = useQuery<
    QueryPageableResponse<ItemCategory, "itemCategoriesFind">,
    QueryPageableRequest<ItemCategoryFindRequest>
  >(GetItemCategoriesForLookup, {
    variables: {
      request: {
        take: 1000,
        skip: 0,
        level: ItemCategoryLevel.Category,
      },
    },
  });

  const { data: subcategoryData, refetch: refetchSubcategory } = useQuery<
    QueryPageableResponse<ItemCategory, "itemCategoriesFind">,
    QueryPageableRequest<ItemCategoryFindRequest>
  >(GetItemCategoriesForLookup, {
    variables: {
      request: {
        take: 1000,
        skip: 0,
        level: ItemCategoryLevel.Subcategory,
      },
    },
    skip: !categoryId,
  });

  const { data: subcategory2Data, refetch: refetchSubcategory2 } = useQuery<
    QueryPageableResponse<ItemCategory, "itemCategoriesFind">,
    QueryPageableRequest<ItemCategoryFindRequest>
  >(GetItemCategoriesForLookup, {
    variables: {
      request: {
        take: 1000,
        skip: 0,
        level: ItemCategoryLevel.Subcategory2,
      },
    },
    skip: !subcategoryId,
  });

  // Helper functions
  const mapToOptions = (
    data?: QueryPageableResponse<ItemCategory, "itemCategoriesFind">
  ) => {
    return (data?.itemCategoriesFind.data ?? []).map((e) => ({
      value: e.id || 0,
      display: `${e.id} - ${e.title}`,
      item: e,
    }));
  };

  // Filter options based on parent
  const categoryOptions = mapToOptions(categoryData);
  const subcategoryOptions = mapToOptions(subcategoryData).filter(
    (e) => e.item?.parent?.id === categoryId
  );
  const subcategory2Options = mapToOptions(subcategory2Data).filter(
    (e) => e.item?.parent?.id === subcategoryId
  );

  // Function to refresh all categories
  const refreshAllCategories = async () => {
    await refetchCategory();
    if (categoryId) {
      await refetchSubcategory();
    }
    if (subcategoryId) {
      await refetchSubcategory2();
    }
  };

  return {
    categoryData,
    subcategoryData,
    subcategory2Data,
    categoryOptions,
    subcategoryOptions,
    subcategory2Options,
    refreshAllCategories,
  };
};
